import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { Observable, combineLatest, filter, map, merge, take, takeUntil, withLatestFrom } from "rxjs";
import { Select, ofActionSuccessful } from "@ngxs/store";
import { TranslateModule } from "@ngx-translate/core";

import { DataGridComponent } from "@app/shared/components/smart-data-grid/data-grid/data-grid.component";
import { AppDataState } from "@app/shared/stores/app-data.state";
import { GetAchievement, ImportAchievementData } from "@app/shared/actions/app-data.action";
import { NgxsActionName, NgxsFormPath } from "@app/shared/constants";
import { SearchByItem, WebApiResponse, WebApiResponseBase } from "@app/shared/classes/general";
import { AngularMaterialModule } from "@app/shared/modules/angular-material.module";
import { LoadingSpinnerComponent } from "@app/shared/components/loading-spinner/loading-spinner.component";
import { SmartSearchFormComponent } from "@app/shared/components/smart-search-form/smart-search-form.component";
import * as AppDataAction from "@app/shared/actions/app-data.action";
import { MessageDataComponent } from "@app/shared/components/message-data/message-data.component";
import { SpinnerButtonComponent } from "@app/shared/components/spinner-button/spinner-button.component";
import { PageBasedComponent } from "@app/pages/page.based.component";
import { ImportDataResultComponent } from "@app/pages/import-data-result/import-data-result.component";
import { ImportResult } from "@app/shared/classes/import-result";
import { DataGridColumnDef, GetAchievementSearchByList } from "./achievement.constant";
import { Achievement } from "@app/shared/classes/achievement";
import { CommonPageFilterSetting, CommonSearchCriteria, CreatePageFilterSettingWithLstChangeOnSort } from "@app/shared/models/common-data.model";
import { AchievementEditModalDialogComponent } from "@app/shared/components/edit-modal-dialog/achievement-edit-modal-dialog/achievement-edit-modal-dialog.component";
import { ExcelImportButtonComponent } from "@app/shared/components/excel-import-button/excel-import-button.component";
import { CsvImportType } from "@app/shared/enums";
import { DisableEditButtonDirective } from "@app/shared/directive/disable-edit-button.directive";

@Component({
  selector: "app-achievement",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingSpinnerComponent,
    DataGridComponent,
    MessageDataComponent,
    CommonModule,
    TranslateModule,
    ExcelImportButtonComponent,
    AngularMaterialModule,
    SmartSearchFormComponent,
    SpinnerButtonComponent,
    ImportDataResultComponent,
    DisableEditButtonDirective,
  ],
  templateUrl: "./achievement.component.html",
  styleUrl: "./achievement.component.css",
})
export class AchievementComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.ACHIEVEMENT_SEARCH_FORM;
  selectedItems: Achievement[] = [];

  dataSource$: Observable<Achievement[]>;
  achievementSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentAchievementSearchBy) currentAchievementSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_ACHIEVEMENT)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_ACHIEVEMENT)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_ACHIEVEMENT)) response$: Observable<WebApiResponse<Achievement[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_ACHIEVEMENT)) errorResponse$: Observable<WebApiResponseBase>;

  @Select(AppDataState.taskExecuting(NgxsActionName.IMPORT_ACHIEVEMENT_DATA)) importing$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.IMPORT_ACHIEVEMENT_DATA)) importResponse$: Observable<ImportResult[]>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_ACHIEVEMENT_DATA)) importErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_ACHIEVEMENT;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentAchievementSearchBy$.pipe(take(1)).subscribe((searchCriteria) => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map((errorResponse) => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.data)
    );
    this.totalCount$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.totalCount)
    );

    this.achievementSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$]).pipe(map((c) => GetAchievementSearchByList(c)));

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.ImportAchievementData), withLatestFrom(this.importResponse$, this.importErrorResponse$, this.profileMe$), takeUntil(this.destroy$))
      .subscribe(([_, importResponse, importErrorResponse, profileMe]) =>
        this.showImportResult(importResponse as ImportResult[], importErrorResponse, "ml_143952", "ml_143950", profileMe, this.onSearchWithLastModifyPageFilter)
      );
  }

  rowSelected = (data: Achievement[]) => {
    this.selectedItems = data ?? [];
  };

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetAchievement());
  };

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetAchievement(null));
  };

  onSearchWithLastModifyPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetAchievement(CreatePageFilterSettingWithLstChangeOnSort(this.functionName)));
  };

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_ACHIEVEMENT;
    this.store.dispatch(new GetAchievement(info));
  };

  onEdit = () => {
    this.dialog.open(AchievementEditModalDialogComponent, {
      width: "700px",
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true,
    });
  };

  onImport = (file: File) => {
    this.excelImportService.OnFileSlected(
      file,
      [],
      (fileData) => {
        var importData = this.csvService.GetImportObjectFromCsvData(CsvImportType.ACHIEVEMENT, fileData);
        if (importData.length === 0) {
          this.showImportEmptyDataResult();
        } else {
          this.store.dispatch(new ImportAchievementData(importData));
        }
      },
      this.showImportEmptyDataResult
    );
  };
}
